/**
 * Lead Submission — surfacing "what the client actually told us".
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS (owner ask, 2026-06-21)
 * ─────────────────────────────────────────────────────────────────────────
 *  Every website / free-tool lead lands as a FLAT `leads/{id}` doc carrying
 *  the client's own answers — the contact-form message (`responses.note_oneline`),
 *  the E2 property score + observations + room zones + uploaded map, the E4
 *  area scores + flags, etc. (see AV_Website/site/FORMS_SCHEMA.md).
 *
 *  The portal's pure `Lead` model (lib/leads.ts) is a thin pipeline record;
 *  `leadStore.flatDocToLead` deliberately reconstructs only the pipeline-
 *  relevant fields and DROPS the rich submission payload. So the new
 *  engagement-flow Leads screen showed name/phone/email/owner but no longer
 *  surfaced what the client filled in.
 *
 *  This module is the SURFACING seam (not a data rebuild):
 *   1. `extractSubmission(rawDoc)` — PURE — pulls the submission payload off
 *      the raw Firestore doc into a typed `LeadSubmission`.
 *   2. `flatDocToLead` attaches it to `lead.submission` (an optional carry
 *      field on the pure Lead). It round-trips inside the `notes_internal`
 *      pipeline snapshot — NO firestore.rules change, same discipline as
 *      `trashed`/agreed-price.
 *   3. `SubmissionDetails.tsx` renders it on the lead record.
 *
 *  The humanize maps + level→colour are ported from the LEGACY
 *  `_legacy/lib/leadHumanize.ts` (the screen that already knew how to render
 *  every field) and adapted to the live portal's design tokens (white canvas;
 *  --good/--warn/--crit/--ink). Keep the maps in sync with the wizards.
 * ─────────────────────────────────────────────────────────────────────────
 */

/* ════════════════════ Humanize maps (ported from legacy) ════════════════════ */

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  residential_apartment: 'Residential — apartment / flat',
  residential_independent: 'Residential — independent house / villa',
  plot: 'Plot (undeveloped land)',
  commercial: 'Commercial',
  mixed: 'Mixed use',
  // FORMS_SCHEMA service_enquiry values
  apartment: 'Apartment / flat',
  villa: 'Villa / independent house',
};

const PROPERTY_STATUS_LABELS: Record<string, string> = {
  considering_buy: 'Considering — may buy',
  about_to_buy: 'About to buy — final stage',
  already_own: 'Already own — evaluating',
  under_construction: 'Under construction',
};

const PROPERTY_SHAPE_LABELS: Record<string, string> = {
  regular_rectangle: 'Regular rectangle',
  square: 'Square',
  gaumukhi: 'Gaumukhi — narrow at entrance, broad at rear',
  shermukhi: 'Shermukhi — broad at entrance, narrow at rear',
  ne_extended: 'Regular with north-east extension',
  ne_cut: 'Regular with north-east corner cut / missing',
  sw_cut: 'Regular with south-west corner cut / missing',
  l_shape: 'L-shape',
  triangular: 'Triangular',
  irregular: 'Irregular / other',
};

const PROPERTY_SLOPE_LABELS: Record<string, string> = {
  flat: 'Flat / level',
  slope_to_ne: 'Slopes toward north-east (ideal)',
  slope_to_n: 'Slopes toward north',
  slope_to_e: 'Slopes toward east',
  slope_to_sw: 'Slopes toward south-west (reversed — unfavourable)',
  slope_to_s: 'Slopes toward south',
  slope_to_w: 'Slopes toward west',
  irregular: 'Irregular / mixed',
  dont_know: 'Not sure',
};

const ENTRY_FACING_LABELS: Record<string, string> = {
  N: 'North (N)',
  NE: 'North-East (NE)',
  E: 'East (E)',
  SE: 'South-East (SE)',
  S: 'South (S)',
  SW: 'South-West (SW)',
  W: 'West (W)',
  NW: 'North-West (NW)',
};

const ZONE_LABELS: Record<string, string> = {
  N: 'North',
  NNE: 'North-Northeast (NNE)',
  NE: 'North-East',
  ENE: 'East-Northeast (ENE)',
  E: 'East',
  ESE: 'East-Southeast (ESE)',
  SE: 'South-East',
  SSE: 'South-Southeast (SSE)',
  S: 'South',
  SSW: 'South-Southwest (SSW)',
  SW: 'South-West',
  WSW: 'West-Southwest (WSW)',
  W: 'West',
  WNW: 'West-Northwest (WNW)',
  NW: 'North-West',
  NNW: 'North-Northwest (NNW)',
  na: 'Not applicable',
  unsure: 'Not sure',
};

const PRIMARY_CONCERN_LABELS: Record<string, string> = {
  career: 'Career & growth',
  wealth: 'Wealth',
  health: 'Health',
  relationships: 'Relationships',
};

/** Contact-form `intent` picklist (FORMS_SCHEMA §4.1). */
const CONTACT_INTENT_LABELS: Record<string, string> = {
  property_selection: 'Selecting / buying a property',
  home_review: 'Reviewing an existing home',
  design_new: 'Designing a new building',
  general: 'General enquiry',
};

/** service_enquiry `service_code` (FORMS_SCHEMA §4.2). */
const SERVICE_CODE_LABELS: Record<string, string> = {
  L1A: 'L1A — Property Selection',
  L1B: 'L1B — Clarity Path · Darshan',
  L2: 'L2 — Alignment Path · Nidana',
  L3: 'L3 — Full Vastu',
};

/** E1/E3 guide topics. */
const GUIDE_TOPIC_LABELS: Record<string, string> = {
  property_buying: 'Before-You-Buy Guide',
  growth_blocks: 'Growth Blocks Guide',
};

function lookup(
  map: Record<string, string>,
  code: string | undefined | null,
  fallback = '—',
): string {
  if (code == null || code === '') return fallback;
  if (Object.prototype.hasOwnProperty.call(map, code)) return map[code];
  return String(code);
}

export const humanizeLead = {
  propertyType: (c?: string | null) => lookup(PROPERTY_TYPE_LABELS, c),
  propertyStatus: (c?: string | null) => lookup(PROPERTY_STATUS_LABELS, c),
  shape: (c?: string | null) => lookup(PROPERTY_SHAPE_LABELS, c),
  slope: (c?: string | null) => lookup(PROPERTY_SLOPE_LABELS, c),
  entryFacing: (c?: string | null) => lookup(ENTRY_FACING_LABELS, c),
  zone: (c?: string | null) => lookup(ZONE_LABELS, c),
  primaryConcern: (c?: string | null) => lookup(PRIMARY_CONCERN_LABELS, c, ''),
  contactIntent: (c?: string | null) => lookup(CONTACT_INTENT_LABELS, c, ''),
  serviceCode: (c?: string | null) => lookup(SERVICE_CODE_LABELS, c),
  guideTopic: (c?: string | null) => lookup(GUIDE_TOPIC_LABELS, c),
  /** Join a code list into a readable "A, B and C" phrase. */
  codeList: (codes?: unknown): string => {
    if (!Array.isArray(codes) || codes.length === 0) return '';
    const arr = codes.map((c) => String(c)).filter(Boolean);
    if (arr.length === 0) return '';
    if (arr.length === 1) return arr[0];
    if (arr.length === 2) return arr.join(' and ');
    return arr.slice(0, -1).join(', ') + ' and ' + arr[arr.length - 1];
  },
};

/* ════════════════════ Level → portal token (white canvas) ════════════════════ */

export type SubmissionLevel = 'green' | 'amber' | 'red';

/** Normalise the wizards' two observation shapes onto one level. */
export function normalizeLevel(raw: unknown): SubmissionLevel | null {
  const s = String(raw ?? '').toLowerCase();
  if (s === 'green' || s === 'good') return 'green';
  if (s === 'amber' || s === 'warn' || s === 'yellow') return 'amber';
  if (s === 'red' || s === 'crit' || s === 'critical') return 'red';
  return null;
}

/** Portal design-token colour pair per level (text + wash). Live UI tokens. */
export const LEVEL_TOKEN: Record<
  SubmissionLevel,
  { fg: string; wash: string; label: string }
> = {
  green: { fg: 'var(--good)', wash: 'var(--good-wash)', label: 'supports' },
  amber: { fg: 'var(--warn)', wash: 'var(--warn-wash)', label: 'watch' },
  red: { fg: 'var(--crit)', wash: 'var(--crit-wash)', label: 'concern' },
};

/* ════════════════════ The submission payload (typed) ════════════════════ */

export interface SubmissionObservation {
  level: SubmissionLevel | null;
  text: string;
}

export interface SubmissionRoom {
  label: string; // already humanized room name
  zone: string; // already humanized zone
}

export interface SubmissionArea {
  key: 'career' | 'wealth' | 'health' | 'relationships';
  label: string; // "Career & growth"
  level: SubmissionLevel | null;
  score: number | null;
  band: string; // e.g. "Well supported"
}

/**
 * Everything the client told us, normalised for display. Optional fields are
 * only present when the source supplied them — the renderer never shows an
 * empty row.
 */
export interface LeadSubmission {
  /** The RAW website source code (E1/E2/E3/E4/contact/service_enquiry/newsletter). */
  rawSource: string;
  /** Human label as written by the site (`source_label`). */
  sourceLabel: string;
  /** Which page on the site the form lived on (`sourcePage`). */
  sourcePage: string;
  /** Site app version stamp (e.g. "vivaswan-v1.0.0", "v1.7.0"). */
  appVersion: string;
  /** ISO submission time, best-effort. */
  submittedAt: string;
  country: string;

  /* — contact / service_enquiry — */
  contactIntent?: string; // humanized
  serviceWanted?: string; // humanized service code or intent
  message?: string; // responses.note_oneline (the client's own words)
  city?: string;
  consentMarketing?: boolean | null;

  /* — guide (E1 / E3) — */
  guideRequested?: string;

  /* — property answers (E2 / E4) — */
  score?: number | null;
  propertyTypeRaw?: string;
  propertyStatusRaw?: string;
  propertyShapeRaw?: string;
  propertySlopeRaw?: string;
  entryFacingRaw?: string;
  entryCodes?: string[];
  primaryConcernRaw?: string;

  rooms?: SubmissionRoom[];
  observations?: SubmissionObservation[]; // E2
  flags?: SubmissionObservation[]; // E4
  areas?: SubmissionArea[]; // E4

  /* — uploaded artifacts (E2/E4 map) — */
  mapUrl?: string;
  mapFilename?: string;
  mapNorth?: string;
  gmapLink?: string;

  /** No-Go soft-fail reasons (HTML-bearing strings — sanitize on render). */
  nogoReasons?: string[];
}

/* ════════════════════ Extraction (PURE) ════════════════════ */

const ROOM_KEY_LABELS: Record<string, string> = {
  kitchen: 'Kitchen',
  bedroom: 'Bedroom',
  master_bedroom: 'Master bedroom',
  other_bedroom: 'Other bedroom',
  children_bedroom: "Children's bedroom",
  pooja: 'Pooja space',
  toilet: 'Toilet',
  main_toilet: 'Main toilet',
  additional_toilet: 'Additional toilet',
  study: 'Study',
  living_room: 'Living room',
  dining_room: 'Dining room',
};

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

function toISO(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') return v;
  const maybe = v as { toDate?: () => Date; seconds?: number };
  if (typeof maybe.toDate === 'function') {
    try {
      return maybe.toDate().toISOString();
    } catch {
      return '';
    }
  }
  if (v instanceof Date) return v.toISOString();
  if (typeof maybe.seconds === 'number') {
    return new Date(maybe.seconds * 1000).toISOString();
  }
  return '';
}

function normObservations(raw: unknown): SubmissionObservation[] {
  if (!Array.isArray(raw)) return [];
  const out: SubmissionObservation[] = [];
  for (const o of raw) {
    if (!o || typeof o !== 'object') continue;
    const obs = o as Record<string, unknown>;
    const level = normalizeLevel(obs.level ?? obs.color);
    const text =
      str(obs.text) ||
      [str(obs.title), str(obs.body)].filter(Boolean).join(' — ');
    if (text) out.push({ level, text });
  }
  return out;
}

/** Build the room list from BOTH the dynamic `rooms[]` and legacy zone keys. */
function extractRooms(responses: Record<string, unknown>): SubmissionRoom[] {
  const out: SubmissionRoom[] = [];
  const dynamic = responses.rooms;
  if (Array.isArray(dynamic)) {
    for (const r of dynamic) {
      if (!r || typeof r !== 'object') continue;
      const room = r as Record<string, unknown>;
      const type = str(room.type);
      const zone = str(room.zone);
      if (!type && !zone) continue;
      out.push({
        label: ROOM_KEY_LABELS[type] || type || 'Room',
        zone: humanizeLead.zone(zone || null),
      });
    }
  }
  // Legacy flat zone keys (E2 v1 / E4) — only if no dynamic rooms supplied.
  if (out.length === 0) {
    for (const key of [
      'kitchen',
      'bedroom',
      'children_bedroom',
      'pooja',
      'toilet',
      'study',
    ]) {
      const zone = str(responses[key]);
      if (zone) {
        out.push({
          label: ROOM_KEY_LABELS[key] || key,
          zone: humanizeLead.zone(zone),
        });
      }
    }
  }
  return out;
}

function extractAreas(raw: unknown): SubmissionArea[] {
  if (!raw || typeof raw !== 'object') return [];
  const areas = raw as Record<string, unknown>;
  const out: SubmissionArea[] = [];
  for (const key of ['career', 'wealth', 'health', 'relationships'] as const) {
    const a = areas[key];
    if (!a || typeof a !== 'object') continue;
    const area = a as Record<string, unknown>;
    out.push({
      key,
      label: humanizeLead.primaryConcern(key) || key,
      level: normalizeLevel(area.color),
      score: typeof area.score === 'number' ? area.score : null,
      band: str(area.band) || str(area.label),
    });
  }
  return out;
}

/**
 * PURE — extract the submission payload from a RAW flat `leads/{id}` doc.
 * Returns `null` for portal-native sources (manual / WhatsApp inbound) that
 * carry no website submission payload — the caller leaves `lead.submission`
 * undefined and the renderer shows nothing.
 */
export function extractSubmission(
  data: Record<string, unknown>,
): LeadSubmission | null {
  const rawSource = str(data.source);
  const responses = (data.responses as Record<string, unknown>) ?? {};

  // Portal-native / offline channels carry no website submission payload.
  // The pure-snapshot path (portal-created leads) also lands here as a raw
  // doc, but those have no `responses` / `source_label` website fields.
  const NON_WEBSITE = new Set([
    'whatsapp_inbound',
    'whatsapp',
    'manual_referral',
    'referral',
    'portal_manual',
  ]);
  if (NON_WEBSITE.has(rawSource)) return null;

  const sub: LeadSubmission = {
    rawSource,
    sourceLabel: str(data.source_label),
    sourcePage: str(data.sourcePage),
    appVersion: str(data.app_version),
    submittedAt:
      toISO(data.created_at) || toISO(data.createdAt) || toISO(data.updated_at),
    country: str(data.country),
  };

  // ── contact ──
  if (rawSource === 'contact') {
    sub.contactIntent = humanizeLead.contactIntent(str(responses.intent));
    sub.serviceWanted = sub.contactIntent;
    sub.message = str(responses.note_oneline);
    sub.consentMarketing =
      typeof responses.consent_marketing === 'boolean'
        ? (responses.consent_marketing as boolean)
        : null;
  }

  // ── service_enquiry ──
  if (rawSource === 'service_enquiry') {
    sub.serviceWanted = humanizeLead.serviceCode(str(responses.service_code));
    sub.propertyTypeRaw = str(responses.property_type);
    sub.city = str(responses.city);
    sub.message = str(responses.note_oneline);
  }

  // ── guide leads (E1 / E3) ──
  if (rawSource === 'E1' || rawSource === 'E3') {
    sub.guideRequested =
      humanizeLead.guideTopic(str(responses.guide_topic)) ||
      (rawSource === 'E1' ? 'Before-You-Buy Guide' : 'Growth Blocks Guide');
    sub.consentMarketing =
      typeof responses.consent_marketing === 'boolean'
        ? (responses.consent_marketing as boolean)
        : null;
  }

  // ── property-score (E2) + home-assessment (E4) ──
  if (rawSource === 'E2' || rawSource === 'E4') {
    if (typeof data.score === 'number') sub.score = data.score as number;
    sub.propertyTypeRaw = str(responses.property_type);
    sub.propertyStatusRaw = str(responses.property_status);
    sub.propertyShapeRaw = str(responses.property_shape);
    sub.propertySlopeRaw = str(responses.property_slope);
    sub.entryFacingRaw = str(responses.entry_facing);
    sub.primaryConcernRaw = str(responses.primary_concern);
    const codes = responses.entry_codes;
    if (Array.isArray(codes) && codes.length > 0) {
      sub.entryCodes = codes.map((c) => String(c)).filter(Boolean);
    }
    const rooms = extractRooms(responses);
    if (rooms.length > 0) sub.rooms = rooms;
    const obs = normObservations(data.observations);
    if (obs.length > 0) sub.observations = obs;
    const flags = normObservations(data.flags);
    if (flags.length > 0) sub.flags = flags;
    const areas = extractAreas(data.areas);
    if (areas.length > 0) sub.areas = areas;
  }

  // ── uploaded map artifact (E2/E4) ──
  const artifacts = data.artifacts;
  if (artifacts && typeof artifacts === 'object') {
    const art = artifacts as Record<string, unknown>;
    const mapUrl = str(art.map_url);
    if (mapUrl) sub.mapUrl = mapUrl;
    sub.mapFilename = str(art.map_filename) || str(art.map_file_name);
    sub.mapNorth = str(art.map_north);
    sub.gmapLink = str(art.gmap_link);
  }

  // ── No-Go soft-fail reasons ──
  const nogo = data.nogo;
  if (nogo && typeof nogo === 'object') {
    const reasons = (nogo as Record<string, unknown>).reasons;
    if (Array.isArray(reasons)) {
      const list = reasons.map((r) => String(r)).filter(Boolean);
      if (list.length > 0) sub.nogoReasons = list;
    }
  }

  return sub;
}
